/**
 * Clinic Info Placeholders
 *
 * Contains all placeholder values used in templates that need to be replaced
 * with actual clinic information during site generation.
 */

// Clinic name placeholders used in different themes
export const CLINIC_NAME_PLACEHOLDERS = [
  "Premium Clinic",
  "Bloom Beauty",
  "Modern Medical",
  "Your Clinic Name",
  "MediCare Plus",
  "LUXE Clinic",
] as const;

// Phone number placeholders
export const PHONE_PLACEHOLDERS = ["02-123-4567", "02-888-9999", "02-999-8888"] as const;

// Email placeholders
export const EMAIL_PLACEHOLDERS = [
  "contact@premiumclinic.com",
  "hello@bloombeauty.com",
  "info@modernmedical.com",
  "info@medicareplus.com",
  "hello@luxeclinic.com",
] as const;

// Address placeholders
export const ADDRESS_PLACEHOLDERS = [
  "123 Medical Center Road, Sukhumvit, Bangkok 10110",
  "123 Medical Center Road, Sukhumvit Soi 55, Watthana, Bangkok 10110, Thailand",
  "123 Medical Center Road, Bangkok",
  "456 Thonglor, Bangkok",
  "456 Thonglor Soi 10, Sukhumvit 55, Watthana, Bangkok 10110",
  "789 Hospital Road, Ratchadaphisek, Bangkok 10400",
  "789 Hospital Road, Bangkok",
  "123 Sukhumvit Road, Watthana, Bangkok 10110",
  "123 Sukhumvit Road, Bangkok",
] as const;

// LINE ID placeholders
export const LINE_ID_PLACEHOLDERS = ["@premiumclinic", "@bloombeauty"] as const;

// Default fallback values
export const DEFAULT_CLINIC_VALUES = {
  name: "My Clinic",
  phone: "02-XXX-XXXX",
  email: "contact@clinic.com",
  address: "Your Address",
  lineId: "@clinic",
} as const;

// Types for clinic info
export interface ClinicInfo {
  name?: string;
  clinicName?: string; // alias for name (used in some places)
  phone?: string;
  email?: string;
  address?: string;
  lineId?: string;
  logo?: string;
}

/**
 * Replace all clinic info placeholders in content string
 * @param content - The content string containing placeholders
 * @param clinicInfo - The clinic info to replace placeholders with
 * @returns The content with placeholders replaced
 */
export function replaceClinicInfoPlaceholders(content: string, clinicInfo: ClinicInfo): string {
  let result = content;

  // Normalize clinic name (support both 'name' and 'clinicName')
  const name = clinicInfo.name || clinicInfo.clinicName || DEFAULT_CLINIC_VALUES.name;
  const phone = clinicInfo.phone || DEFAULT_CLINIC_VALUES.phone;
  const email = clinicInfo.email || DEFAULT_CLINIC_VALUES.email;
  const address = clinicInfo.address || DEFAULT_CLINIC_VALUES.address;
  const lineId = clinicInfo.lineId || DEFAULT_CLINIC_VALUES.lineId;

  // Replace clinic names
  for (const placeholder of CLINIC_NAME_PLACEHOLDERS) {
    result = result.split(placeholder).join(name);
  }

  // Replace phone numbers
  for (const placeholder of PHONE_PLACEHOLDERS) {
    result = result.split(placeholder).join(phone);
  }
  // Additional phone patterns
  result = result.split("Call 02-123-4567").join(`Call ${phone}`);

  // Replace emails
  for (const placeholder of EMAIL_PLACEHOLDERS) {
    result = result.split(placeholder).join(email);
  }

  // Replace addresses
  for (const placeholder of ADDRESS_PLACEHOLDERS) {
    result = result.split(placeholder).join(address);
  }

  // Replace LINE IDs
  for (const placeholder of LINE_ID_PLACEHOLDERS) {
    result = result.split(placeholder).join(lineId);
  }
  // LINE URL pattern
  result = result
    .split("https://line.me/ti/p/@premiumclinic")
    .join(lineId ? `https://line.me/ti/p/${lineId}` : "https://line.me");

  return result;
}
